import { cn } from "@/lib/utils";
import { ButtonHTMLAttributes, ReactNode } from "react";

type Size = "small" | "medium";

type TextButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: Size;
  /** Optional icon, rendered to the left of the text at the text's size. */
  icon?: ReactNode;
};

const sizeClasses: Record<Size, string> = {
  small: "text-xs [&>span>svg]:size-3.5",
  medium: "text-sm [&>span>svg]:size-4",
};

/**
 * A label-only action: no fill, no border, no shadow. For the secondary action
 * that sits in a header row or beside a title, where a real button's chrome
 * would outweigh what it does ("Chain Info", "Clear Chain", "Show all").
 *
 * The padding is cancelled by matching negative margins, so the tap target is
 * larger than the text without moving anything around it.
 */
export const TextButton = ({
  size = "small",
  icon,
  className,
  children,
  disabled,
  ...props
}: TextButtonProps) => (
  <button
    type="button"
    className={cn(
      "flex shrink-0 items-center gap-x-1 whitespace-nowrap font-semibold",
      "-m-1.5 p-1.5",
      sizeClasses[size],
      disabled
        ? "text-foreground-disabled cursor-not-allowed"
        : "text-foreground-muted hover:text-foreground transition-colors",
      className,
    )}
    disabled={disabled}
    {...props}
  >
    {icon && <span className="flex shrink-0 items-center">{icon}</span>}
    {children}
  </button>
);
